import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '~/components/button';
import { api } from '~/lib/api';

export const Route = createFileRoute('/_authed/founder/decisions')({
  component: DecisionsPage,
});

function DecisionsPage() {
  const qc = useQueryClient();
  const [headline, setHeadline] = useState('');
  const [why, setWhy] = useState('');
  const [alternatives, setAlternatives] = useState('');
  const [expected, setExpected] = useState('');
  const [confidence, setConfidence] = useState(70);
  const list = useQuery({ queryKey: ['decisions'], queryFn: api.decisionsList });
  const review = useMutation({ mutationFn: api.decisionsWeeklyReview });

  const create = useMutation({
    mutationFn: api.decisionsCreate,
    onSuccess: () => {
      setHeadline('');
      setWhy('');
      setAlternatives('');
      setExpected('');
      setConfidence(70);
      qc.invalidateQueries({ queryKey: ['decisions'] });
    },
  });

  return (
    <section className="mx-auto max-w-2xl px-4 py-12">
      <h1 className="text-2xl font-semibold tracking-tight">decision log</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        30 seconds at the moment. honest reflection later.
      </p>

      <form
        className="mt-8 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!headline.trim()) return;
          create.mutate({
            headline: headline.trim(),
            why: why.trim() || undefined,
            alternatives: alternatives
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean),
            expectedOutcome: expected.trim() || undefined,
            confidence,
          });
        }}
      >
        <input
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="headline of the decision"
          className="w-full h-11 rounded-[var(--radius)] border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <textarea
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          rows={2}
          placeholder="why? (optional)"
          className="w-full rounded-[var(--radius)] border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <textarea
          value={alternatives}
          onChange={(e) => setAlternatives(e.target.value)}
          rows={2}
          placeholder="alternatives considered, one per line (optional)"
          className="w-full rounded-[var(--radius)] border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <textarea
          value={expected}
          onChange={(e) => setExpected(e.target.value)}
          rows={2}
          placeholder="expected outcome (optional)"
          className="w-full rounded-[var(--radius)] border border-input bg-background p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <div>
          <label className="text-xs text-muted-foreground">confidence: {confidence}/100</label>
          <input
            type="range"
            min={0}
            max={100}
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
            className="w-full"
          />
        </div>
        <Button type="submit" disabled={!headline.trim() || create.isPending}>
          {create.isPending ? 'saving…' : 'log decision'}
        </Button>
      </form>

      <section className="mt-12">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm uppercase tracking-wide text-muted-foreground">recent</h2>
          <Button size="sm" variant="secondary" disabled={review.isPending} onClick={() => review.mutate()}>
            {review.isPending ? 'reviewing…' : 'weekly review'}
          </Button>
        </div>
        {review.data && (
          <article className="mt-4 rounded-[var(--radius)] border border-border p-4 text-sm whitespace-pre-wrap">
            {review.data.review}
          </article>
        )}
        <ul className="mt-4 space-y-2">
          {(list.data?.decisions ?? []).map((d) => (
            <li key={String(d.id)} className="rounded-[var(--radius)] border border-border p-3 text-sm">
              <div className="font-medium">{String(d.headline ?? '(untitled)')}</div>
              <div className="text-xs text-muted-foreground mt-1">
                confidence {String(d.confidence ?? '?')}/100 ·{' '}
                {new Date(Number(d.createdAt)).toLocaleString()}
              </div>
              {Boolean(d.why) && <div className="mt-2">{String(d.why)}</div>}
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
